import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { money } from "@/lib/ui";
import useIsMobile from "@/lib/useIsMobile";
import { FollowableName } from "@/components/FollowableName";
import { Trophy, Search, Loader2, Crown } from "lucide-react";

function fmtWhen(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return null; }
}
const titleCase = (s) => (s || "").replace(/\w\S*/g, (t) => t[0].toUpperCase() + t.slice(1).toLowerCase());

const ASSOCS = [
  { key: "CPRA", label: "CPRA", color: "#C8000F" },
  { key: "CCA", label: "CCA", color: "#8C6239" },
  { key: "PRCA", label: "PRCA", color: "#003087" },
];

function Row({ rank, name, hometown, earnings, extra, meCut, cfr, mine, m, title, cfrAmt, total, combined }) {
  return (
    <div data-testid={`standings-row-${rank}`}
      className={`flex items-center gap-3 px-3 py-2.5 border-b last:border-b-0 ${m ? "border-white/5" : "border-[#F1F3F5]"} ${mine ? (m ? "bg-[#C8000F]/15" : "bg-[#C8000F]/8") : ""} ${meCut ? "border-b-2 border-b-[#B8860B]/60" : ""}`}>
      <span className={`w-7 text-center font-display font-bold text-sm ${cfr ? "text-[#3B6B1E]" : "text-slate-400"}`}>{rank}</span>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-semibold truncate flex items-center gap-1.5 ${title}`}>
          <FollowableName name={name} />
          {mine ? <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#C8000F] text-white">YOU</span> : null}
          {rank === 1 ? <Crown className="w-3.5 h-3.5 text-[#B8860B]" /> : null}
        </div>
        {hometown ? <div className="text-[11px] text-slate-400 truncate">{hometown}</div> : null}
      </div>
      {extra != null ? <span className="text-[11px] text-slate-400 hidden sm:block w-16 text-right">{extra}</span> : null}
      <div className="w-28 text-right shrink-0">
        <div className="font-display font-bold text-sm text-[#3B6B1E]">{money(combined ? total : earnings)}</div>
        {combined && cfrAmt > 0 ? <div className="text-[10px] text-slate-400 leading-tight">S {money(earnings)} · CFR {money(cfrAmt)}</div> : null}
      </div>
    </div>
  );
}

function EventTabs({ events, value, onChange, labelFn, m }) {
  return (
    <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-1 px-1" data-testid="standings-events">
      {events.map((e) => {
        const key = typeof e === "string" ? e : e.key;
        const label = labelFn ? labelFn(e) : (typeof e === "string" ? e : e.label);
        return (
          <button key={key} onClick={() => onChange(key)} data-testid={`standings-event-${String(key).replace(/\s/g, "-")}`}
            className={`shrink-0 text-xs font-bold px-3 py-2 rounded-lg transition-colors ${key === value
              ? "bg-[#C8000F] text-white"
              : m ? "bg-white/5 text-slate-300 hover:bg-white/10" : "bg-[#F1F3F5] text-slate-600 hover:bg-[#E4E7EB]"}`}>
            {label}
          </button>
        );
      })}
    </div>
  );
}

function SearchBox({ q, setQ, m, card }) {
  return (
    <div className={`flex items-center gap-2 mt-2 mb-3 rounded-lg border px-3 ${card}`}>
      <Search className="w-4 h-4 text-slate-400 shrink-0" />
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search athlete or hometown" data-testid="standings-search"
        className={`flex-1 bg-transparent py-2.5 text-sm focus:outline-none ${m ? "text-slate-100 placeholder:text-slate-500" : "text-[#14181F] placeholder:text-slate-400"}`} />
    </div>
  );
}

// ---------------- CPRA ----------------
function CpraStandings({ m, title, card }) {
  const [board, setBoard] = useState(null);
  const [event, setEvent] = useState(null);
  const [q, setQ] = useState("");
  const [me, setMe] = useState("");
  useEffect(() => {
    api.get("/standings/board").then((r) => { setBoard(r.data); if (r.data.events?.length) setEvent(r.data.events[0].event); }).catch(() => setBoard({ events: [] }));
    api.get("/me/competitor").then((r) => setMe((r.data?.name || "").trim().toLowerCase())).catch(() => {});
  }, []);
  const current = useMemo(() => (board?.events || []).find((e) => e.event === event), [board, event]);
  const rows = useMemo(() => {
    const list = current?.rows || [];
    if (!q.trim()) return list;
    const needle = q.trim().toLowerCase();
    return list.filter((r) => (r.name || "").toLowerCase().includes(needle) || (r.hometown || "").toLowerCase().includes(needle));
  }, [current, q]);
  if (board === null) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>;
  if ((board.events || []).length === 0) return <Empty m={m} title={title} msg="Standings will appear here once the CPRA season money is imported." />;
  const updated = fmtWhen(board?.posted) || fmtWhen(board?.updated_at);
  const anyCfr = (board.events || []).some((e) => e.has_cfr);
  return (
    <>
      <p className="text-xs text-slate-400 mb-2">{board?.year ? `${board.year} CPRA season` : "CPRA season"}{anyCfr && board?.cfr_live_round ? ` + CFR (R${board.cfr_live_round})` : ""}{updated ? ` · updated ${updated}` : ""} · {anyCfr ? "ranked by Season + CFR money" : "top 12 make the CFR"}</p>
      <EventTabs events={(board.events || []).map((e) => e.event)} value={event} onChange={setEvent} m={m}
        labelFn={(e) => e === "TR Header" ? "Team Roping (Header)" : e === "TR Heeler" ? "Team Roping (Heeler)" : e} />
      <SearchBox q={q} setQ={setQ} m={m} card={card} />
      {current?.cfr_cutoff ? <div className="text-[11px] text-slate-400 mb-2">CFR cut line (12th by season): <span className="font-bold text-[#B8860B]">{money(current.cfr_cutoff)}</span></div> : null}
      <div className={`rounded-xl border overflow-hidden ${card}`} data-testid="standings-table">
        {rows.map((r) => (
          <Row key={`${r.rank}-${r.name}`} rank={r.rank} name={r.name} hometown={r.hometown} earnings={r.earnings}
            cfrAmt={r.cfr} total={r.total} combined={current?.has_cfr}
            extra={r.entered != null ? `${r.entered} entered` : null} cfr={r.rank <= 12} meCut={r.rank === 12 && !q.trim()}
            mine={me && (r.name || "").trim().toLowerCase() === me} m={m} title={title} />
        ))}
        {rows.length === 0 && <div className="px-3 py-8 text-center text-sm text-slate-400">No athletes match “{q}”.</div>}
      </div>
      <p className="text-[11px] text-slate-400 mt-2">Source: rodeocanada.com/standings</p>
    </>
  );
}

// ---------------- CCA ----------------
function CcaStandings({ m, title, card }) {
  const [data, setData] = useState(null);
  const [event, setEvent] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get("/cca/standings").then((r) => { setData(r.data); if (r.data.events?.length) setEvent(r.data.events[0].event); }).catch(() => setData({ events: [] }));
  }, []);
  const current = useMemo(() => (data?.events || []).find((e) => e.event === event), [data, event]);
  const rows = useMemo(() => {
    const list = current?.rows || [];
    if (!q.trim()) return list;
    const n = q.trim().toLowerCase();
    return list.filter((r) => (r.name || "").toLowerCase().includes(n) || (r.hometown || "").toLowerCase().includes(n));
  }, [current, q]);
  if (data === null) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#8C6239]" /></div>;
  if ((data.events || []).length === 0) return <Empty m={m} title={title} msg="No CCA standings available right now." />;
  return (
    <>
      <p className="text-xs text-slate-400 mb-2">Canadian Cowboys Association{data?.as_of ? ` · as of ${data.as_of}` : ""}</p>
      <EventTabs events={(data.events || []).map((e) => e.event)} value={event} onChange={setEvent} labelFn={(e) => titleCase(typeof e === "string" ? e : e)} m={m} />
      <SearchBox q={q} setQ={setQ} m={m} card={card} />
      <div className={`rounded-xl border overflow-hidden ${card}`} data-testid="standings-table">
        {rows.map((r, i) => (
          <Row key={`${r.position}-${r.name}-${i}`} rank={r.position} name={r.name} hometown={r.hometown} earnings={r.earnings}
            extra={r.rodeos != null ? `${r.rodeos} rodeos` : null} cfr={r.position <= 12} m={m} title={title} />
        ))}
        {rows.length === 0 && <div className="px-3 py-8 text-center text-sm text-slate-400">No athletes match “{q}”.</div>}
      </div>
      <p className="text-[11px] text-slate-400 mt-2">Source: canadiancowboys.ca</p>
    </>
  );
}

// ---------------- PRCA ----------------
function PrcaStandings({ m, title, card }) {
  const [events, setEvents] = useState([]);
  const [event, setEvent] = useState("AA");
  const [type, setType] = useState("world");
  const [circuits, setCircuits] = useState([]);
  const [circuitId, setCircuitId] = useState("");
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/prca/events").then((r) => setEvents((r.data || []).filter((e) => e.abbrev !== "AA" || true))).catch(() => setEvents([]));
    api.get("/prca/circuits").then((r) => { setCircuits(r.data || []); if (r.data?.length) setCircuitId(String(r.data[0].id)); }).catch(() => setCircuits([]));
  }, []);
  useEffect(() => {
    if (type === "circuit" && !circuitId) return;
    setRows(null);
    const cp = type === "circuit" ? `&circuit_id=${circuitId}` : "";
    api.get(`/prca/standings?event=${event}&type=${type}${cp}`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [event, type, circuitId]);

  const shown = useMemo(() => {
    const list = rows || [];
    if (!q.trim()) return list;
    const n = q.trim().toLowerCase();
    return list.filter((r) => (r.name || "").toLowerCase().includes(n) || (r.hometown || "").toLowerCase().includes(n));
  }, [rows, q]);

  const evTabs = events.length ? events.map((e) => ({ key: e.abbrev, label: e.name })) : [{ key: "AA", label: "All-Around" }];

  return (
    <>
      <div className="flex items-center gap-1.5 mb-3" data-testid="prca-type-toggle">
        {[{ v: "world", l: "World" }, { v: "circuit", l: "Circuit" }].map((o) => (
          <button key={o.v} onClick={() => setType(o.v)} data-testid={`prca-type-${o.v}`}
            className={`text-xs font-bold px-3 py-1.5 rounded-lg ${type === o.v ? "bg-[#003087] text-white" : m ? "bg-white/5 text-slate-300" : "bg-[#F1F3F5] text-slate-600"}`}>{o.l}</button>
        ))}
        {type === "circuit" && (
          <select value={circuitId} onChange={(e) => setCircuitId(e.target.value)} data-testid="prca-circuit-select"
            className={`text-xs rounded-lg border px-2 py-1.5 ${m ? "bg-white/5 border-white/10 text-slate-200" : "bg-white border-[#D7DBE0] text-[#14181F]"}`}>
            {circuits.map((c) => <option key={c.id} value={c.id} className="text-black">{c.name}</option>)}
          </select>
        )}
      </div>
      <EventTabs events={evTabs} value={event} onChange={setEvent} m={m} />
      <SearchBox q={q} setQ={setQ} m={m} card={card} />
      {rows === null ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#003087]" /></div>
      ) : (
        <div className={`rounded-xl border overflow-hidden ${card}`} data-testid="standings-table">
          {shown.map((r, i) => (
            <Row key={`${r.place}-${r.name}-${i}`} rank={r.place} name={r.name} hometown={r.hometown} earnings={r.earnings} m={m} title={title} />
          ))}
          {shown.length === 0 && <div className="px-3 py-8 text-center text-sm text-slate-400">No standings to show{q ? ` for “${q}”` : ""}.</div>}
        </div>
      )}
      <p className="text-[11px] text-slate-400 mt-2">Source: prorodeo.com · {type === "circuit" ? "Circuit" : "World"} standings</p>
    </>
  );
}

function Empty({ m, title, msg }) {
  return (
    <div className={`rounded-xl border border-dashed p-8 text-center ${m ? "border-white/15" : "border-[#D7DBE0]"}`}>
      <Trophy className="w-8 h-8 mx-auto mb-3 text-[#C8000F]" />
      <div className={`font-display font-bold ${title}`}>Nothing here yet</div>
      <p className="text-sm text-slate-400 mt-1">{msg}</p>
    </div>
  );
}

export default function Standings() {
  const m = useIsMobile();
  const [assoc, setAssoc] = useState("CPRA");
  const page = m ? "bg-[#0C1017] min-h-screen text-slate-200 pb-24" : "";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const headBorder = m ? "border-white/10" : "border-[#E4E7EB]";

  return (
    <div className={page} data-testid="standings-page">
      <div className={`px-4 sm:px-6 py-5 border-b ${headBorder}`}>
        <div className="flex items-center gap-2 mb-3">
          <Trophy className="w-6 h-6 text-[#C8000F]" />
          <h1 className={`font-display font-bold text-xl ${title}`}>Standings</h1>
        </div>
        <div className="flex gap-1.5" data-testid="standings-assoc-tabs">
          {ASSOCS.map((a) => (
            <button key={a.key} onClick={() => setAssoc(a.key)} data-testid={`standings-assoc-${a.key}`}
              className={`text-sm font-bold px-4 py-2 rounded-lg transition-colors ${assoc === a.key ? "text-white" : m ? "bg-white/5 text-slate-300 hover:bg-white/10" : "bg-[#F1F3F5] text-slate-600 hover:bg-[#E4E7EB]"}`}
              style={assoc === a.key ? { background: a.color } : {}}>{a.label}</button>
          ))}
        </div>
      </div>
      <div className="px-4 sm:px-6 py-4 max-w-3xl">
        {assoc === "CPRA" && <CpraStandings m={m} title={title} card={card} />}
        {assoc === "CCA" && <CcaStandings m={m} title={title} card={card} />}
        {assoc === "PRCA" && <PrcaStandings m={m} title={title} card={card} />}
      </div>
    </div>
  );
}
